/**
 * StreakShareCard — iter-184-2.
 *
 * Downloadable "share card" that fires when a user crosses a meaningful
 * streak milestone (30d, 90d). The card is rendered on a hidden
 * `<canvas>` so it can be exported as a PNG without extra dependencies
 * (no html2canvas / dom-to-image). One pure 2D canvas, ~12 KB JS.
 *
 * Why this exists: a happy freelancer who just hit 30 consecutive days
 * of cashflow discipline is the cheapest acquisition channel we have.
 * Giving them a polished, brand-stamped image they can post to LinkedIn
 * or Instagram Stories turns the gamification into organic referral.
 *
 * The card is portrait-formatted (1080x1350) to match the Instagram
 * Story aspect ratio (4:5) which also looks fine reposted to LinkedIn
 * and Twitter as a regular image.
 */
import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Download, Share2, X, Flame } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { api } from "../../lib/api";
import { tx } from "../../i18n";

const CARD_W = 1080;
const CARD_H = 1350;

// Map streak milestone slugs → days + label keys.
const MILESTONES = {
  streak_30d: { days: 30, accent: "#f97316" },
  streak_90d: { days: 90, accent: "#a855f7" },
};

function drawCard(canvas, { days, accent, userName, lang, shareUrl }) {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  canvas.width = CARD_W;
  canvas.height = CARD_H;

  // Background — deep navy with subtle vignette so the orange/violet
  // flame pops without looking like a stock template.
  const bg = ctx.createLinearGradient(0, 0, 0, CARD_H);
  bg.addColorStop(0, "#0f172a");
  bg.addColorStop(1, "#020617");
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, CARD_W, CARD_H);

  // Decorative dots — light, irregular, top-right + bottom-left
  ctx.fillStyle = "rgba(148, 163, 184, 0.08)";
  for (let i = 0; i < 40; i += 1) {
    const x = Math.random() * CARD_W;
    const y = Math.random() * CARD_H;
    const r = Math.random() * 3 + 0.6;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }

  // Lumfi wordmark — top-left
  ctx.fillStyle = "#cbd5e1";
  ctx.font = "600 38px Inter, system-ui, -apple-system, sans-serif";
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  ctx.fillText("Lumfi", 80, 80);
  ctx.fillStyle = "#64748b";
  ctx.font = "500 22px Inter, system-ui, -apple-system, sans-serif";
  ctx.fillText("Cash Pilot", 80, 130);

  // Big flame disc (radial gradient w/ accent)
  const cx = CARD_W / 2;
  const cy = 520;
  const ring = ctx.createRadialGradient(cx, cy, 40, cx, cy, 260);
  ring.addColorStop(0, accent);
  ring.addColorStop(1, accent + "00"); // fade to transparent
  ctx.fillStyle = ring;
  ctx.beginPath();
  ctx.arc(cx, cy, 260, 0, Math.PI * 2);
  ctx.fill();

  // Flame emoji (rendered as text — works cross-platform because the
  // browser falls back to the system emoji font; we ship no images).
  ctx.font = "300px system-ui, 'Apple Color Emoji', 'Segoe UI Emoji'";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("🔥", cx, cy); // design-system: ALLOW user-copy

  // Big number — the streak count
  ctx.fillStyle = "#ffffff";
  ctx.font = "800 220px Inter, system-ui, -apple-system, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";
  ctx.fillText(String(days), cx, 920);

  // "days streak" label below the number
  ctx.fillStyle = accent;
  ctx.font = "700 56px Inter, system-ui, -apple-system, sans-serif";
  const daysLabel = tx(lang, "days streak", "días de racha", "dias de sequência");
  ctx.fillText(daysLabel, cx, 1000);

  // Sub-line: "of financial discipline" / "de control financiero"
  ctx.fillStyle = "#cbd5e1";
  ctx.font = "500 34px Inter, system-ui, -apple-system, sans-serif";
  ctx.fillText(
    tx(lang,
      "of financial discipline",
      "de control financiero",
      "de controle financeiro"),
    cx, 1060);

  // User name (if provided) — small caption above CTA
  if (userName) {
    ctx.fillStyle = "#94a3b8";
    ctx.font = "500 28px Inter, system-ui, -apple-system, sans-serif";
    ctx.fillText(
      tx(lang, `${userName} on Lumfi`, `${userName} en Lumfi`, `${userName} no Lumfi`),
      cx, 1160);
  }

  // CTA pill — accent background, white text. We embed the user's
  // trackable referral URL ("lumfi.cash/?ref=<code>") so any
  // visitor who signs up via the shared image earns the user the
  // ``first_referred`` achievement. Falls back to the bare host
  // when the referral fetch hasn't resolved yet (cheap caller).
  const ctaY = 1230;
  const ctaUrl = shareUrl || "lumfi.cash";
  const ctaText = tx(lang,
    `Start your streak → ${ctaUrl}`,
    `Empieza tu racha → ${ctaUrl}`,
    `Comece sua sequência → ${ctaUrl}`);
  ctx.font = "600 30px Inter, system-ui, -apple-system, sans-serif";
  const ctaW = ctx.measureText(ctaText).width + 60;
  const ctaX = cx - ctaW / 2;
  ctx.fillStyle = accent;
  ctx.beginPath();
  if (ctx.roundRect) ctx.roundRect(ctaX, ctaY, ctaW, 60, 30);
  else ctx.rect(ctaX, ctaY, ctaW, 60);
  ctx.fill();
  ctx.fillStyle = "#ffffff";
  ctx.fillText(ctaText, cx, ctaY + 40);
}

export function StreakShareCard({ open, onOpenChange, slug, userName, lang }) {
  const canvasRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const meta = MILESTONES[slug];

  // iter-184-3 — Pull the user's referral code on-demand so the CTA
  // becomes a trackable invite link. Cached for the session by
  // react-query; failures fall back to the bare host (no nag).
  const { data: referral } = useQuery({
    queryKey: ["me-referral"],
    queryFn: async () => (await api.get("/me/referral")).data,
    staleTime: 24 * 60 * 60 * 1000,
    retry: false,
    enabled: open,
  });

  useEffect(() => {
    if (!open || !meta) return undefined;
    // Radix Dialog portals the content, so the canvas ref isn't
    // populated on the same tick `open` flips to true. Defer one
    // frame so the canvas is guaranteed to be mounted before we
    // touch its 2D context.
    const id = requestAnimationFrame(() => {
      if (canvasRef.current) {
        drawCard(canvasRef.current, {
          days: meta.days,
          accent: meta.accent,
          userName,
          lang,
          shareUrl: referral?.short_url,
        });
      }
    });
    return () => cancelAnimationFrame(id);
  }, [open, slug, userName, lang, meta, referral?.short_url]);

  if (!meta) return null;

  const filename = `lumfi-streak-${meta.days}d.png`;

  const downloadPng = async () => {
    if (!canvasRef.current) return;
    setBusy(true);
    try {
      const blob = await new Promise((resolve) =>
        canvasRef.current.toBlob(resolve, "image/png", 0.95),
      );
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } finally {
      setBusy(false);
    }
  };

  const nativeShare = async () => {
    if (!canvasRef.current || !navigator.share) return downloadPng();
    setBusy(true);
    try {
      const blob = await new Promise((resolve) =>
        canvasRef.current.toBlob(resolve, "image/png", 0.95),
      );
      if (!blob) return;
      const file = new File([blob], filename, { type: "image/png" });
      // Some browsers expose share() but not canShare(files) — fall back
      // to a plain text+url share when image isn't accepted.
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: tx(lang,
            `${meta.days}-day Lumfi streak`,
            `Racha de ${meta.days} días en Lumfi`,
            `Sequência de ${meta.days} dias no Lumfi`),
          text: tx(lang,
            `Just hit ${meta.days} consecutive days of financial discipline on Lumfi 🔥`, // design-system: ALLOW user-copy
            `Acabo de cumplir ${meta.days} días seguidos de control financiero en Lumfi 🔥`, // design-system: ALLOW user-copy
            `Acabei de completar ${meta.days} dias seguidos de controle financeiro no Lumfi 🔥`), // design-system: ALLOW user-copy
          url: referral?.share_url || "https://lumfi.cash",
        });
      } else {
        await navigator.share({
          title: tx(lang, "Lumfi streak", "Racha Lumfi", "Sequência Lumfi"),
          text: tx(lang,
            `${meta.days}-day Lumfi streak 🔥`, // design-system: ALLOW user-copy
            `${meta.days} días de racha en Lumfi 🔥`, // design-system: ALLOW user-copy
            `${meta.days} dias de sequência no Lumfi 🔥`), // design-system: ALLOW user-copy
          url: referral?.share_url || "https://lumfi.cash",
        });
      }
    } catch {
      // User cancelled native share — ignore, no error UX.
    } finally {
      setBusy(false);
    }
  };

  const supportsShare = typeof navigator !== "undefined" && !!navigator.share;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md p-0 overflow-hidden"
        data-testid="streak-share-card-dialog"
      >
        <DialogTitle className="sr-only">
          {tx(lang,
            `${meta.days}-day streak share card`,
            `Tarjeta para compartir racha de ${meta.days} días`,
            `Cartão para compartilhar sequência de ${meta.days} dias`)}
        </DialogTitle>
        <DialogDescription className="sr-only">
          {tx(lang,
            `Download or share a polished image celebrating your ${meta.days}-day Lumfi streak.`,
            `Descarga o comparte una imagen para celebrar tu racha de ${meta.days} días en Lumfi.`,
            `Baixe ou compartilhe uma imagem para comemorar sua sequência de ${meta.days} dias no Lumfi.`)}
        </DialogDescription>
        <div className="relative">
          <button /* design-system: ALLOW legacy-todo */
            type="button"
            onClick={() => onOpenChange(false)}
            className="absolute top-3 right-3 z-10 h-8 w-8 rounded-full bg-slate-900/40 text-white hover:bg-slate-900/60 grid place-items-center"
            data-testid="streak-share-close"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
          {/* The canvas is rendered at full resolution but scaled down via
              CSS so the preview fits nicely in the dialog. */}
          <canvas
            ref={canvasRef}
            className="w-full h-auto block bg-slate-900"
            data-testid="streak-share-card-canvas"
          />
        </div>
        <div className="p-4 flex flex-col gap-2 bg-white">
          <p className="text-sm text-slate-700 flex items-center gap-2">
            <Flame className="h-4 w-4 text-orange-500" />
            {tx(lang,
              `You hit a ${meta.days}-day streak — share it.`,
              `Cumpliste ${meta.days} días de racha — compártelo.`,
              `Você atingiu uma sequência de ${meta.days} dias — compartilhe.`)}
          </p>
          <div className="flex gap-2">
            <Button
              className="flex-1"
              onClick={downloadPng}
              disabled={busy}
              data-testid="streak-share-download-btn"
            >
              <Download className="h-4 w-4 mr-2" />
              {tx(lang, "Download", "Descargar", "Baixar")}
            </Button>
            {supportsShare && (
              <Button
                variant="outline"
                className="flex-1"
                onClick={nativeShare}
                disabled={busy}
                data-testid="streak-share-native-btn"
              >
                <Share2 className="h-4 w-4 mr-2" />
                {tx(lang, "Share", "Compartir", "Compartilhar")}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
